"use client";

import { publicService } from "@/services/public.service";
import { useQuery } from "@tanstack/react-query";

// ── Query keys ────────────────────────────────────────────────────────────────
export const PUBLIC_KEYS = {
  hero: ["public", "hero"] as const,
  categories: ["public", "categories"] as const,
  experienceSection: ["public", "experiences", "section"] as const,
  experiences: (params: object) => ["public", "experiences", params] as const,
  experience: (id: string) => ["public", "experiences", id] as const,
  serviceSection: ["public", "services", "section"] as const,
  serviceFilters: ["public", "service-filters"] as const,
  services: (params: object) => ["public", "services", params] as const,
  service: (id: string) => ["public", "services", id] as const,
  propertyFilters: ["public", "property-filters"] as const,
  properties: (params: object) => ["public", "properties", params] as const,
  property: (id: string) => ["public", "properties", id] as const,
  contact: ["public", "contact"] as const,
  about: ["public", "about"] as const,
  terms: ["public", "terms"] as const,
  blogs: (params: object) => ["public", "blogs", params] as const,
  blog: (id: string) => ["public", "blogs", id] as const,
};

// ── Hero ──────────────────────────────────────────────────────────────────────
export function useHero() {
  return useQuery({
    queryKey: PUBLIC_KEYS.hero,
    queryFn: () => publicService.getHero(),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Categories ────────────────────────────────────────────────────────────────
export function useCategories() {
  return useQuery({
    queryKey: PUBLIC_KEYS.categories,
    queryFn: () => publicService.getCategories(),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Experiences ───────────────────────────────────────────────────────────────
export function useExperienceSection() {
  return useQuery({
    queryKey: PUBLIC_KEYS.experienceSection,
    queryFn: () => publicService.getExperienceSection(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useExperiences(
  params: { page?: number; limit?: number; categoryId?: string } = {},
) {
  return useQuery({
    queryKey: PUBLIC_KEYS.experiences(params),
    queryFn: () => publicService.getExperiences(params),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useExperience(id: string) {
  return useQuery({
    queryKey: PUBLIC_KEYS.experience(id),
    queryFn: () => publicService.getExperience(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Services ──────────────────────────────────────────────────────────────────
export function useServiceSection() {
  return useQuery({
    queryKey: PUBLIC_KEYS.serviceSection,
    queryFn: () => publicService.getServiceSection(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useServices(
  params: { page?: number; limit?: number; filterId?: string } = {},
) {
  return useQuery({
    queryKey: PUBLIC_KEYS.services(params),
    queryFn: () => publicService.getServices(params),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function usePublicService(id: string) {
  return useQuery({
    queryKey: PUBLIC_KEYS.service(id),
    queryFn: () => publicService.getService(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Property Filters ──────────────────────────────────────────────────────────
export function usePropertyFilters() {
  return useQuery({
    queryKey: PUBLIC_KEYS.propertyFilters,
    queryFn: () => publicService.getPropertyFilters(),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Service Filters ───────────────────────────────────────────────────────────
export function useServiceFilters() {
  return useQuery({
    queryKey: PUBLIC_KEYS.serviceFilters,
    queryFn: () => publicService.getServiceFilters(),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Properties ────────────────────────────────────────────────────────────────
export function usePublicProperties(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    filterId?: string;
    minPrice?: number;
    maxPrice?: number;
    city?: string;
  } = {},
) {
  return useQuery({
    queryKey: PUBLIC_KEYS.properties(params),
    queryFn: () => publicService.getProperties(params),
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

// Service fee + VAT (dashboard-managed) for price summaries.
export function usePricingRates() {
  return useQuery({
    queryKey: ["public", "pricing"],
    queryFn: () => publicService.getPricingRates(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTestimonials() {
  return useQuery({
    queryKey: ["public", "testimonials"],
    queryFn: () => publicService.getTestimonials(),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePropertyCities() {
  return useQuery({
    queryKey: ["public", "property-cities"],
    queryFn: () => publicService.getPropertyCities(),
    staleTime: 10 * 60 * 1000,
  });
}

export function usePublicProperty(id: string) {
  return useQuery({
    queryKey: PUBLIC_KEYS.property(id),
    queryFn: () => publicService.getProperty(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Contact ───────────────────────────────────────────────────────────────────
export function useContact() {
  return useQuery({
    queryKey: PUBLIC_KEYS.contact,
    queryFn: () => publicService.getContact(),
    staleTime: 10 * 60 * 1000,
  });
}

// ── About ─────────────────────────────────────────────────────────────────────
export function useAbout() {
  return useQuery({
    queryKey: PUBLIC_KEYS.about,
    queryFn: () => publicService.getAbout(),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Terms ─────────────────────────────────────────────────────────────────────
// ── «استكشف الوحدات» banner ───────────────────────────────────────────────────
export function usePropertiesBanner() {
  return useQuery({
    queryKey: ["public", "properties-banner"],
    queryFn: () => publicService.getPropertiesBanner(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTerms() {
  return useQuery({
    queryKey: PUBLIC_KEYS.terms,
    queryFn: () => publicService.getTerms(),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Blogs ─────────────────────────────────────────────────────────────────────
export function useBlogs(params: { page?: number; limit?: number; search?: string } = {}) {
  return useQuery({
    queryKey: PUBLIC_KEYS.blogs(params),
    queryFn: () => publicService.getBlogs(params),
    placeholderData: (prev) => prev,
    staleTime: 2 * 60 * 1000,
  });
}

export function useBlog(id: string) {
  return useQuery({
    queryKey: PUBLIC_KEYS.blog(id),
    queryFn: () => publicService.getBlog(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}
